import { CbhFeatureFlag } from "@src/appV2/FeatureFlags";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { showErrorToast, showSuccessToast } from "@src/appV2/lib/Notifications";
import { Button, Card, Typography } from "antd";
import { useState } from "react";

import {
  useCreateShiftBoardAccessToken,
  useGetLatestShiftBoardAccessToken,
} from "../api/useShiftBoardAccessToken";

const { Paragraph } = Typography;

interface ShiftBoardLinkButtonProps {
  workplaceId: string;
}

export function ShiftBoardLinkButton({ workplaceId }: ShiftBoardLinkButtonProps) {
  const isShiftBoardEnabled = useCbhFlag(CbhFeatureFlag.ALCHEMY_SHIFT_BOARD_ROLLOUT, {
    defaultValue: false,
  });

  const [isGenerating, setIsGenerating] = useState(false);

  const { data: latestToken, refetch } = useGetLatestShiftBoardAccessToken(workplaceId, {
    enabled: isShiftBoardEnabled,
  });
  const createTokenMutation = useCreateShiftBoardAccessToken();

  if (!isShiftBoardEnabled) {
    return null;
  }

  const { link } = latestToken?.data?.attributes ?? {};
  const url = link ? new URL(link) : null;

  const handleGenerateLink = async () => {
    setIsGenerating(true);
    try {
      await createTokenMutation.mutateAsync({ workplaceId });
      await refetch();
      showSuccessToast("New shift board link generated successfully");
    } catch {
      showErrorToast("Failed to generate shift board link");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Card title="Shift Board Link">
      {url && (
        <Paragraph copyable={{ text: url.toString() }} style={{ marginTop: 16 }}>
          {url.origin}
          {url.pathname.split("/").slice(0, 3).join("/")}
        </Paragraph>
      )}
      <Button type="primary" disabled={isGenerating} onClick={handleGenerateLink}>
        {isGenerating ? "Generating..." : "Generate a new link"}
      </Button>
    </Card>
  );
}
